import 'antd/dist/antd.css';
import '../../normalize.css';

import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import {
  HashRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
  useHistory,
} from 'react-router-dom';
import { ThunderboltOutlined } from '@ant-design/icons';
import { withTranslation, useTranslation } from 'react-i18next';

import { APP_VERSION_QUERY_KEY } from '@Constants/queryKeyConstants';
import { API_RETURN_CODE } from '@Constants/apiReturnCode';
import { useAppVersion } from '@Hooks/useQueries/useSystemQueries';
import { systemPresentVersion } from '@Utils/commonParameters';
import { useIsDevelopment } from '@Utils/commonHooks';

import AntdErrorAlert from '@Components/antdErrorAlert';

import * as systemActions from '@/actions/systemActions';

import {
  Login,
  Pricing,
  RegisterRoute,
  ForgotPasswordRoute,
  DashboardRoute,
  GuestSurveyFormRoute,
  IndexPageAppRoute,
  BoothPointRoute,
  GuestRoute,
  LotteryRoute,
  NotFound,
  Site,
  GuestNotFound,
  Maintenance,
} from '../../pages/app';

import PrivateRoute from '../private';
import RedirectRoute from '../redirect';

import styles from './appRoute.scss';

function UpdatingVersionButton() {
  const { systemVersion } = useSelector((state) => state.systemSetting);

  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [newVersion, setNewVersion] = useState(systemPresentVersion);
  const [versionMustBeUpdated, setVersionMustBeUpdated] = useState(false);

  const appVersionQuery = useAppVersion({});

  const handleUpdateVersion = () => {
    return window.location.reload();
  };

  // 切換頁面就 call 檢查版號
  useEffect(() => {
    if (systemVersion) {
      queryClient.invalidateQueries([APP_VERSION_QUERY_KEY]);
    }
  }, [location.pathname, systemVersion, queryClient]);

  useEffect(() => {
    if (appVersionQuery.isLoading || appVersionQuery.isFetching || !appVersionQuery.data) return;
    const { data } = appVersionQuery;
    const { mustBeUpdated, version, returnCode } = data;

    setNewVersion(version);
    setVersionMustBeUpdated(mustBeUpdated);

    dispatch(systemActions.setIsMaintenance(returnCode === API_RETURN_CODE.module.maintenance));

    if (returnCode === API_RETURN_CODE.success && location.pathname?.includes('/maintenance')) {
      history.replace('/home');
    }
  }, [appVersionQuery.isLoading, appVersionQuery.isFetching, appVersionQuery.data]);

  // 版本不同，強制更新 true，則重新整理
  // 風險: 如果忘記改版號(systemVersion)但 db 已經有新的版號，就會一直重新整理頁面
  // TODO: 可以於 sessionStorage 計算 call hanldeUpdateVersion 幾次，超過一定數量就不再 call function
  useEffect(() => {
    if (versionMustBeUpdated && newVersion !== systemVersion) {
      handleUpdateVersion();
    }
  }, [versionMustBeUpdated, newVersion, systemVersion]);

  // 沒有強制更新的時候，user 可以點此按鈕 reload page fetch the newest version
  // 目前比較少用到此邏輯，因為每次更改版號現在都設定成"強制更新"會比較保險
  return (
    <>
      {newVersion !== undefined &&
        versionMustBeUpdated !== undefined &&
        systemVersion !== newVersion &&
        !versionMustBeUpdated && (
          <button
            type="button"
            className={styles['update-version-button']}
            onClick={handleUpdateVersion}
            message={t('common.clickToUpdateVersion')}
          >
            <ThunderboltOutlined />
          </button>
        )}
    </>
  );
}

function AppRoute(props) {
  const { setWindowInnerWidth, i18n, setIsOnline, isOnline } = props;

  const { t } = useTranslation();

  const isDevelopment = useIsDevelopment();

  const { isMaintenance } = useSelector((state) => state.systemSetting);

  window.onresize = () => {
    if (window.innerWidth) {
      setWindowInnerWidth(window.innerWidth);
    }
  };

  window.addEventListener('load', () => {
    function handleNetworkCondition(event) {
      if (event.type === 'online') {
        window.location.reload();
      }
      const condition = window.navigator.onLine;
      setIsOnline(condition);
      return undefined;
    }

    window.addEventListener('online', handleNetworkCondition);
    window.addEventListener('offline', handleNetworkCondition);
  });

  useEffect(() => {
    if (window?.navigator?.language) {
      i18n.changeLanguage(window.navigator.language);
    }
  }, []);

  return (
    <Router>
      <UpdatingVersionButton />
      {!isOnline && <AntdErrorAlert errorMessage={t('common.networkErrorMessage')} />}

      <Switch>
        <Route path="/maintenance" component={Maintenance} />
        {isMaintenance && <Redirect to="/maintenance" />}

        <Redirect exact from="/home" to="/" />
        {(isDevelopment === '' || isDevelopment) && <Route path="/site" component={Site} />}
        <RedirectRoute path="/login">
          <Route component={Login} />
        </RedirectRoute>
        <RedirectRoute path="/register">
          <Route component={RegisterRoute} />
        </RedirectRoute>
        <RedirectRoute path="/pricing">
          <Route component={Pricing} />
        </RedirectRoute>
        <Route path="/forgotpassword" component={ForgotPasswordRoute} />
        <Route path="/guestSurveyForm" component={GuestSurveyFormRoute} />
        <Route path="/indexPageApp" component={IndexPageAppRoute} />
        <Route path="/boothPoint" component={BoothPointRoute} />
        <Route path="/guest" component={GuestRoute} />
        <Route path="/winnerlist" component={LotteryRoute} />
        <Route path="/notFound" component={GuestNotFound} />
        <Route path="/error" component={NotFound} />

        <PrivateRoute>
          <DashboardRoute />
        </PrivateRoute>
      </Switch>
    </Router>
  );
}

export default withTranslation()(AppRoute);
